import React, {ButtonHTMLAttributes, ReactNode, useId, useState} from 'react';
import classNames from 'classnames';
import type {LucideIcon} from 'lucide-react';
import {ChevronDownIcon} from 'lucide-react';
import Icon from '../shared/Icon.tsx';
import Tooltip from './Tooltip.tsx';

/*
 * The frame around every creative tool: one toolbar, one tool button, one
 * panel section and one property row, so the whiteboard, Blocks, the 3D
 * scene and vibe coding all feel like the same product. The canvases inside
 * the frame stay as playful as they like.
 */

interface ToolbarProps {
    // What the toolbar holds, for screen readers: "Whiteboard tools".
    label: string;
    children: ReactNode;
    vertical?: boolean;
    // Floats over the canvas (elevation 2) rather than sitting in a panel.
    floating?: boolean;
    className?: string;
}

export const Toolbar: React.FC<ToolbarProps> = ({
    label,
    children,
    vertical,
    floating = true,
    className
}) => (
    <div
        role="toolbar"
        aria-label={label}
        aria-orientation={vertical ? 'vertical' : 'horizontal'}
        className={classNames(
            'mx-toolbar',
            {'mx-toolbar-vertical': vertical, 'mx-toolbar-floating': floating},
            className
        )}
    >
        {children}
    </div>
);

export const ToolbarDivider: React.FC = () => (
    <span className="mx-toolbar-divider" role="separator" />
);

interface ToolButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    // The name, used for the tooltip and the accessible label.
    label: string;
    icon?: LucideIcon;
    shortcut?: string;
    // The selected tool is purple, which is what purple is for.
    selected?: boolean;
    // A swatch or other custom content instead of an icon.
    children?: ReactNode;
    // Draws a small chevron: the button opens a menu.
    hasMenu?: boolean;
}

export const ToolButton: React.FC<ToolButtonProps> = ({
    label,
    icon,
    shortcut,
    selected,
    children,
    hasMenu,
    className,
    id,
    title,
    ...props
}) => {
    const generated = useId().replace(/:/g, '');
    const buttonId = id ?? `tool-${generated}`;

    return (
        <>
            <button
                type="button"
                id={buttonId}
                aria-label={label}
                aria-pressed={selected !== undefined ? selected : undefined}
                // Kept for the existing tests that select toolbar buttons by title.
                title={title ?? label}
                className={classNames(
                    'mx-toolbutton',
                    {active: selected, 'mx-toolbutton-menu': hasMenu},
                    className
                )}
                {...props}
            >
                {children ?? (icon && <Icon icon={icon} size="m" />)}
                {hasMenu && (
                    <Icon icon={ChevronDownIcon} size={10} className="mx-toolbutton-chevron" />
                )}
            </button>
            <Tooltip target={buttonId} shortcut={shortcut}>
                {label}
            </Tooltip>
        </>
    );
};

interface PanelSectionProps {
    title: ReactNode;
    // A small button on the right of the title, e.g. "+" to add.
    actions?: ReactNode;
    children: ReactNode;
    collapsible?: boolean;
    defaultOpen?: boolean;
    className?: string;
}

// A titled section in a tool's side panel.
export const PanelSection: React.FC<PanelSectionProps> = ({
    title,
    actions,
    children,
    collapsible,
    defaultOpen = true,
    className
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const bodyId = useId();

    return (
        <section className={classNames('mx-panel-section', className)}>
            <div className="mx-panel-section-header">
                {collapsible ? (
                    <button
                        type="button"
                        className="mx-panel-section-toggle"
                        aria-expanded={open}
                        aria-controls={bodyId}
                        onClick={() => setOpen(o => !o)}
                    >
                        <Icon
                            icon={ChevronDownIcon}
                            size="xs"
                            className={classNames('mx-panel-chevron', {open})}
                        />
                        <span className="mx-panel-section-title">{title}</span>
                    </button>
                ) : (
                    <span className="mx-panel-section-title">{title}</span>
                )}
                {actions && <span className="mx-panel-section-actions">{actions}</span>}
            </div>
            {open && (
                <div id={bodyId} className="mx-panel-section-body">
                    {children}
                </div>
            )}
        </section>
    );
};

interface PropertyRowProps {
    label: ReactNode;
    // The id of the control, for the label.
    htmlFor?: string;
    children: ReactNode;
    className?: string;
}

// A label on the left and a compact control on the right, 36px tall.
export const PropertyRow: React.FC<PropertyRowProps> = ({
    label,
    htmlFor,
    children,
    className
}) => (
    <div className={classNames('mx-property-row', className)}>
        <label htmlFor={htmlFor} className="mx-property-label">
            {label}
        </label>
        <span className="mx-property-control">{children}</span>
    </div>
);
